// InviteModal.tsx · v12.10.26 (#2135 Mobile = the standard phone input; #2129 Send invite by Email / Text / Both, Both by default) · 2026-10-30 · VoiceSpectrum AI
// InviteModal.tsx · v12.10.11.1 (#1447 never an endless Sending… - Invite sent / Saved - not sent, use Resend) · 2026-10-09
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// InviteModal.tsx - Invite user (#1257, S28/S29; prototype scPaintInvite v1.85): email OR mobile + one or more groups
// (+ optional message). NO name fields - the person fills in their own name and password when they accept. Groups above the
// inviter's own access are disabled ("— exceeds your access"). With both email and mobile the invite goes by email.
import { useEffect, useRef, useState } from 'react';
import { isConfirmCancelled, withConfirm } from '../../components/security/ConfirmItsYou';
import { GroupPicker } from '../../components/security/people';
import { Ic, InlineError, ScModal, digits } from '../../components/security/ui';
import { securityApi, securityErrorMessage, type Group, type InviteSendBy, type UserRow } from '../../lib/securityApi';
import { toast } from '../../lib/toast';
import PhoneInput, { mobileFieldError } from '../../components/PhoneInput';   // v12.10.26 (#2135)
import { toE164 } from '../../lib/phone';
import { deliveryToast } from './userActions';   // v12.10.26 (#2129)

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const INVITE_CLIENT_TIMEOUT_MS = 20_000;   // v12.10.11.1 (#1447)

function defaultGroups(groups: Group[]): number[] {
  const front = groups.find((g) => g.stdKey === 'front' && !g.exceedsMe);
  return front ? [front.id] : [];
}

export default function InviteModal({ groups, users, onClose, onInvited }: {
  groups: Group[]; users: UserRow[]; onClose: () => void; onInvited: (row: UserRow | null) => void;
}) {
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [sel, setSel] = useState<number[]>(() => defaultGroups(groups));
  const [msg, setMsg] = useState('');
  // v12.10.26 (#2135): "incomplete" waits until the field was left or Send pressed; every change re-validates (no stale error).
  const [mobileTouched, setMobileTouched] = useState(false);
  // v12.10.26 (#2129, decided 10/30): Send invite by - Both by default once a mobile is entered.
  const [sendBy, setSendBy] = useState<InviteSendBy>('both');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);
  useEffect(() => { emailRef.current?.focus({ preventScroll: true }); }, []);

  const e = email.trim(), m = mobile.trim();
  const emailErr = e && !EMAIL_RE.test(e) ? 'That doesn’t look like an email address.'
    : e && users.some((u) => (u.email ?? '').toLowerCase() === e.toLowerCase()) ? 'Someone here already uses this email.' : '';
  const mobileE164 = toE164(m);
  const mobileErr = m && !mobileE164 ? (mobileFieldError(m, mobileTouched) || ' ')
    : m && users.some((u) => u.mobile && digits(u.mobile).slice(-10) === digits(m).slice(-10)) ? 'Someone here already uses this number.' : '';
  const valid = (e ? !emailErr : (!!m && !mobileErr)) && !(e && m && mobileErr) && sel.length > 0;
  // v12.10.26 (#2129): both an email and a valid mobile -> the person picks Email / Text / Both (Both by default).
  const choose = !!e && !emailErr && !!mobileE164 && !mobileErr;
  const sendByNow: InviteSendBy = choose ? sendBy : e && !emailErr ? 'email' : 'text';
  const via = choose ? '' : e && !emailErr ? 'The invite goes by email.' : !e && m && !mobileErr ? 'No email, so the invite goes by text.' : '';

  async function send() {
    setMobileTouched(true);
    if (!valid || busy) return;
    setBusy(true); setErr('');
    let timedOut = false;
    try {
      const body = { ...(e ? { email: e } : {}), ...(mobileE164 ? { mobile: mobileE164 } : {}), groups: sel, ...(msg.trim() ? { message: msg.trim() } : {}), sendBy: sendByNow };
      // v12.10.11.1 (#1447): never an endless "Sending…" - the server answers within ~8 s (the send is bounded there), and this
      // request gives up after 20 s whatever happens. The timer starts per attempt, so a Confirm-it's-you pause does not count.
      const row = await withConfirm(() => {
        const ac = new AbortController();
        const timer = window.setTimeout(() => { timedOut = true; ac.abort(); }, INVITE_CLIENT_TIMEOUT_MS);
        return securityApi.invite(body, { signal: ac.signal }).finally(() => window.clearTimeout(timer));
      }, 'You’re inviting someone into your organization.');
      onInvited(row ?? null);
      if (row?.delivery) {
        // v12.10.26 (#2129 #2132): per channel - what went, what didn't and why.
        if (!row.delivery.emailSent && !row.delivery.textSent)
          toast('warning', 'Saved - but the invite was not sent', `${row.delivery.problem ?? ''} Use Resend invite or Copy invite link on the person’s row.`.trim());
        else deliveryToast(e || (mobileE164 ?? m), row.delivery);
      } else if (row && row.inviteSent === false) {
        // UI-PROTO-T56 v1.25 #1447: saved, but the email / text did not go.
        toast('warning', `Saved - but the ${e ? 'email' : 'text'} was not sent`, `The ${e ? 'mail server' : 'text service'} did not answer. Use Resend invite on the person’s row.`);
      } else {
        toast('success', `Invite sent to ${e || m}`, `By ${e ? 'email' : 'text'} - the link works for 7 days.`);
      }
      onClose();
    } catch (x) {
      if (timedOut) {
        // We do not know whether the server got that far - the list is refreshed so the person shows if they were saved.
        onInvited(null);
        toast('warning', 'The invite is taking too long', 'If the person now shows in the list, use Resend invite on their row; otherwise try again.');
        onClose();
      } else if (!isConfirmCancelled(x)) setErr(securityErrorMessage(x, 'The invite wasn’t sent. Try again.'));
    } finally { setBusy(false); }
  }

  return (
    <ScModal onClose={onClose} wide label="Invite user" closeOnScrim={false}>
      <h4>Invite user</h4>
      <p>Enter their email or mobile number and pick their groups. They fill in their own name and set their own password when they accept.</p>
      <div className="sc-inv-grid">
        <div className="mrow"><label htmlFor="inv-email">Email</label>
          <input id="inv-email" ref={emailRef} type="email" autoComplete="off" placeholder="name@clinic.com" value={email} onChange={(x) => setEmail(x.target.value)} />
          {emailErr && <span className="sc-err">{emailErr}</span>}</div>
        <div className="mrow"><label htmlFor="inv-phone">Mobile <span className="mrow-hint">(optional - to also send the invite by text)</span></label>
          {/* v12.10.26 (#2135): the app's standard phone input - formats as you type, paste cleaned */}
          <PhoneInput id="inv-phone" value={mobile} onChange={setMobile} onBlur={() => setMobileTouched(true)} invalid={!!mobileErr.trim()} />
          {mobileErr.trim() && <span className="sc-err">{mobileErr}</span>}
          {!mobileErr && mobileE164 && <span className="sc-idst">✓ Mobile number looks right</span>}</div>
      </div>
      <p className="sc-mp" style={{ marginTop: 6, marginBottom: 0 }}>At least one is needed. The mobile is also kept for text-message codes.</p>
      {choose && (
        <div className="sc-dw-sec" role="radiogroup" aria-label="Send invite by">
          {/* v12.10.26 (#2129): Send invite by - the text is a short link from the practice's VoiceSpectrum number, valid 7 days */}
          <h5>Send invite by</h5>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(['email', 'text', 'both'] as InviteSendBy[]).map((k) => (
              <button key={k} type="button" role="radio" aria-checked={sendBy === k} className={`seg${sendBy === k ? ' on' : ''}`} onClick={() => setSendBy(k)}>
                <span className="seg-dot" />{k === 'email' ? 'Email' : k === 'text' ? 'Text' : 'Both'}
              </button>
            ))}
          </div>
          {sendBy !== 'email' && <p className="sc-mp" style={{ margin: '6px 0 0' }}>The text comes from the practice’s VoiceSpectrum number with a short link (api.voicespectrum.ai/r/…), valid 7 days.</p>}
        </div>
      )}
      <div className="sc-dw-sec">
        <h5>Groups</h5>
        <GroupPicker value={sel} groups={groups} onChange={setSel} />
        {!sel.length && <div className="sc-idst" style={{ marginTop: 6 }}>Pick at least one group.</div>}
      </div>
      <div className="mrow" style={{ marginTop: 12 }}><label htmlFor="inv-msg">Message <span className="mrow-hint">(optional)</span></label>
        <textarea id="inv-msg" placeholder="Added to the invite" value={msg} onChange={(x) => setMsg(x.target.value)} /></div>
      {err && <div style={{ marginBottom: 12 }}><InlineError text={err} /></div>}
      <div className="mact">
        <span className="sc-idst sc-alt">{via}</span>
        <button type="button" className="mc" onClick={onClose}>Cancel</button>
        <button type="button" className="ma" disabled={!valid || busy} onClick={() => void send()}><Ic k="send" /> {busy ? 'Sending…' : 'Send invite'}</button>
      </div>
    </ScModal>
  );
}
